import React, { Component, Fragment } from "reactn";
import { Thumbnail, ThumbnailContainer } from "./ThumbnailGrid";
import Thumbnails from "js/core/models/thumbnails";
import { ds } from "js/core/models/dataService";
import { app } from "js/namespaces";
import { IconMenu } from "js/react/components/UiComponents";
import { Gap5, Gap10, Gap20 } from "js/react/components/Gap";
import { Icon, MenuItem, Tooltip } from "@material-ui/core";
import styled from "styled-components";
import { THUMBNAIL_SIZES } from "common/constants";
import { _ } from "js/vendor";
import getLogger, { LogGroup } from "js/core/logger";
import getUserProfile from "js/core/services/userProfiles";
import { themeColors } from "js/react/sharedStyles";
import WorkspaceController from "js/controllers/WorkspaceController";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { withTranslation } from "react-i18next";
import UpgradeIcon from "js/Components/UpgradeIcon";

const logger = getLogger(LogGroup.THUMBNAIL);

const TeamSlideThumbnailBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 10px;
  label {
    flex-grow: 2;
    text-align: left;
    margin: 0px 10px 0px 0px;
  }
  .MuiIcon-root {
    margin-right: 3px;
    font-size: 18px;
  }
  & > div {
    font-size: 12px;
  }
  .star-button{
    color: ${props => props.liked ? themeColors.yellow : "inherit"};

    &:hover {
      color: ${themeColors.yellow}
    }
  }
  button {
    padding: 2px;
  }
`;

const IconBlueOnHover = styled(Icon)`
  &:hover {
    color: ${themeColors.ui_blue};
  }
`;

const StyledThumbnail = styled(Thumbnail)`
    border: solid 1px #ccc;
    box-shadow: 0px 0px 20px rgba(0,0,0,0.05);
`;

const DisabledSlideIcon = styled.div`
  position:absolute;
  top: 8px;
  right: 8px;
  width: 30px;
  height: 30px;
  z-index: 100;
  border-radius: 50%;
  background: orangered;
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    color: white;
    font-size: 20px;
  }
`;

export const TeamSlideThumbnail = withFirebaseUser(withTranslation()(
    class TeamSlideThumbnail extends Component {
        constructor() {
            super();

            this.state = {
                url: "",
                isVisible: false,
                ItemInfo: null
            };

            this.ref = React.createRef();
        }

        componentDidMount() {
            this.loadItemInfo();

            this.observer = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting === true) {
                    this.observer.disconnect();
                    this.setState({ isVisible: true });
                }
            });

            this.observer.observe(this.ref.current);
        }

        componentDidUpdate(prevProps, prevState, snapshot) {
            const { item } = this.props;
            const { isVisible } = this.state;

            if ((prevState.isVisible !== isVisible && isVisible) || (prevProps.item.id !== item.id) || (prevProps.item.contentModifiedAt !== item.contentModifiedAt)) {
                this.loadThumbnail()
                    .catch(err => logger.error(err, "[TeamSlideThumbnail] this.loadThumbnail() failed", { id: item.id }));
            }

            if (item.description !== prevProps.item.description) {
                this.loadItemInfo();
            }
        }

        componentWillUnmount() {
            this.isUnmounted = true;
            this.observer.disconnect();
        }

        async loadThumbnail() {
            const { item, highQuality, showThemeThumbnails = true } = this.props;
            let url = "";
            this.setState({
                url
            });

            if (showThemeThumbnails && ds.selection.presentation && !ds.selection.presentation.get("isDummy")) {
                const presentationId = ds.selection.presentation.id;
                const firstSlideModifiedAt = ds.selection.presentation.get("firstSlideModifiedAt");
                url = await Thumbnails.getSignedUrlAndLoad(item.id, firstSlideModifiedAt, presentationId, THUMBNAIL_SIZES[highQuality ? "large" : "small"].suffix);
            } else {
                url = await Thumbnails.getSignedUrlAndLoad(item.id, item.contentModifiedAt);
            }

            if (!this.isUnmounted) {
                this.setState({ url });
            }
        }

        handleLikeItem = async () => {
            const { item: { resource }, firebaseUser } = this.props;

            let ratedByUsers = resource.metadata?.ratedByUsers ?? [];
            if (!ratedByUsers.includes(firebaseUser.uid)) {
                ratedByUsers.push(firebaseUser.uid);
            } else {
                ratedByUsers = ratedByUsers.filter(uid => uid !== firebaseUser.uid);
            }

            try {
                await WorkspaceController.updateWorkspaceResource({
                    resourceId: resource.resourceId,
                    resourceType: resource.resourceType,
                    metadata: {
                        ratedByUsers
                    }
                });
            } catch (err) {
                logger.error(err, "[TeamSlideThumbnail] handleLikeItem() failed", { id: resource.resourceId });
            }
        }

        async loadItemInfo() {
            const { item } = this.props;

            try {
                const profile = await getUserProfile(item.createdBy);
                const description = item.description;
                const { t } = this.props;

                const ItemInfo = (
                    <div style={{ padding: 10 }}>
                        {description && <div>{description}</div>}
                        {description && <Gap10 />}
                        <div>{t("Created by")} <strong>{profile.displayName || profile.email || t("Unknown")}</strong></div>
                    </div>
                );

                this.setState({
                    ItemInfo
                });
            } catch (err) {
                logger.error(err, "[TeamSlideThumbnail] this.loadItemInfo() failed", { id: item.id });
            }
        }

        render() {
            const { item, onDoubleClick, selected, isManageLibrary, canUpgradeToManageSharedResources, onEditSlide, onDeleteSlide, onCopySlide, onDuplicateSlide, onMouseDown, t } = this.props;
            const { url, ItemInfo } = this.state;

            let clickProps;
            if (isManageLibrary) {
                clickProps = {
                    onDoubleClick: onDoubleClick
                };
            } else {
                clickProps = {
                    onDoubleClick: onDoubleClick,
                    ...(onMouseDown ? { onMouseDown: onMouseDown } : { onClick: onDoubleClick }),
                };
            }

            const { isTemplate } = item;

            return (
                <ThumbnailContainer ref={this.ref}>
                    <StyledThumbnail
                        {...clickProps}
                        url={url} selected={selected} showSpinner={true}
                        style={canUpgradeToManageSharedResources ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                    >
                        {item.isDisabled && <DisabledSlideIcon><Icon>visibility_off</Icon></DisabledSlideIcon>}
                    </StyledThumbnail>
                    <TeamSlideThumbnailBar liked={item.ratedByUsers && item.ratedByUsers[app.user.id]}>
                        <label>{item.name}</label>
                        <Icon onClick={this.handleLikeItem} className="star-button">star</Icon>
                        <div>{item.ratedByCount}</div>
                        {!isTemplate &&
                            <>
                                <Gap10 />
                                <Icon>cloud_download</Icon>
                                <div>{item.presentationCount}</div>
                            </>
                        }
                        <Gap10 />
                        <Tooltip
                            title={ItemInfo ?? ""}
                            classes={{ popper: "popper-white rounded-5" }}
                            placement="top"
                            arrow
                            interactive
                        >
                            <IconBlueOnHover>info</IconBlueOnHover>
                        </Tooltip>
                        {isManageLibrary &&
                            <Fragment>
                                <Gap20 />
                                <IconMenu>
                                    {canUpgradeToManageSharedResources ? [
                                        <MenuItem key="edit" disabled divider><UpgradeIcon/><Gap5/>{t("Edit Slide...")}</MenuItem>,
                                        <MenuItem key="properties" disabled divider><UpgradeIcon/><Gap5/>{t("View Properties...")}</MenuItem>,
                                        <MenuItem key="copy" disabled divider><UpgradeIcon/><Gap5/>{isTemplate ? t("Copy to Shared Slides...") : t("Copy to Slide Templates...")}</MenuItem>,
                                        <MenuItem key="duplicate" disabled divider><UpgradeIcon/><Gap5/>{t("Duplicate")}</MenuItem>,
                                    ] : [
                                        <MenuItem key="edit" divider onClick={() => onEditSlide(item)}><Icon>edit</Icon>{t("Edit Slide...")}</MenuItem>,
                                        <MenuItem key="properties" divider onClick={onDoubleClick}><Icon>label</Icon>{t("View Properties...")}</MenuItem>,
                                        <MenuItem key="copy" divider onClick={() => onCopySlide(item)}>
                                            <Icon>change_circle</Icon>
                                            {isTemplate ? t("Copy to Shared Slides...") : t("Copy to Slide Templates...")}
                                        </MenuItem>,
                                        <MenuItem key="duplicate" divider onClick={() => onDuplicateSlide(item)}><Icon>file_copy</Icon>{t("Duplicate")}</MenuItem>,
                                    ]}
                                    <MenuItem key="delete" onClick={() => onDeleteSlide(item)}><Icon>delete</Icon>{t("Delete")}</MenuItem>

                                </IconMenu>
                            </Fragment>
                        }
                    </TeamSlideThumbnailBar>
                </ThumbnailContainer>
            );
        }
    }
));
